import marked from 'marked';

const ALLOWED_TAGS = new Set([
  'a',
  'b',
  'blockquote',
  'br',
  'code',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'li',
  'ol',
  'p',
  'pre',
  'strong',
  'ul'
]);

const ALLOWED_ATTRS = {
  a: new Set(['href', 'title', 'target', 'rel']),
  code: new Set(['class']),
  pre: new Set(['class'])
};

const BLOCKED_TAGS = new Set([
  'applet',
  'base',
  'button',
  'embed',
  'form',
  'frame',
  'iframe',
  'input',
  'link',
  'meta',
  'object',
  'script',
  'select',
  'style',
  'textarea'
]);

function sanitizeUrl(value) {
  if (!value) {
    return '';
  }

  const trimmed = value.trim();
  const lower = trimmed.toLowerCase();

  if (
    lower.startsWith('javascript:') ||
    lower.startsWith('data:') ||
    lower.startsWith('vbscript:')
  ) {
    return '';
  }

  return trimmed;
}

function cleanNode(node, doc) {
  if (!node || !node.childNodes) {
    return;
  }

  Array.from(node.childNodes).forEach((child) => {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const tag = child.tagName.toLowerCase();

      if (BLOCKED_TAGS.has(tag)) {
        child.remove();
        return;
      }

      if (!ALLOWED_TAGS.has(tag)) {
        while (child.firstChild) {
          node.insertBefore(child.firstChild, child);
        }
        child.remove();
        return;
      }

      Array.from(child.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase();
        const allowedAttrs = ALLOWED_ATTRS[tag] || new Set();

        if (!allowedAttrs.has(name)) {
          child.removeAttribute(attr.name);
          return;
        }

        if (tag === 'a' && name === 'href') {
          const href = sanitizeUrl(attr.value);
          if (!href) {
            child.removeAttribute('href');
            return;
          }
          child.setAttribute('href', href);
          child.setAttribute('target', '_blank');
          child.setAttribute('rel', 'noopener noreferrer nofollow');
        }
      });

      cleanNode(child, doc);
      return;
    }

    if (child.nodeType === Node.COMMENT_NODE) {
      child.remove();
      return;
    }

    if (child.nodeType !== Node.TEXT_NODE) {
      child.replaceWith(doc.createTextNode(child.textContent || ''));
    }
  });
}

export function sanitizeHtml(content) {
  if (!content) {
    return '';
  }

  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return String(content);
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${content}</div>`, 'text/html');
  const root = doc.body.firstElementChild;

  cleanNode(root, doc);

  return root.innerHTML;
}

export function renderSafeMarkdown(content) {
  if (!content) {
    return '';
  }

  const renderer = new marked.Renderer();
  renderer.link = function(href, title, text) {
    const safeHref = sanitizeUrl(href);
    const titleAttr = title ? ` title="${title}"` : '';

    if (!safeHref) {
      return text;
    }

    return `<a href="${safeHref}"${titleAttr} target="_blank" rel="noopener noreferrer nofollow">${text}</a>`;
  };

  const rendered = marked(String(content), {
    gfm: true,
    breaks: true,
    renderer
  });

  return sanitizeHtml(rendered);
}
